import { useState } from "react";
import { ChevronDown, Link2, ShieldCheck, TriangleAlert } from "lucide-react";

import type { Huerfano, Movimiento, RazonRevision } from "../api/tipos.ts";
import { usarDecidir, usarLibro, usarRevisarAlerta, type Decision } from "../ganchos/datos.ts";
import { usarSesion } from "../sesion/ContextoSesion.tsx";
import { PERMISO } from "../sesion/permisos.ts";
import { fechaCorta, haceRato, pesos, plural, soloHora } from "../formato.ts";
import { RAZON_REVISION } from "../vocabulario.ts";
import { ErrorConsulta, EsqueletoLista } from "../componentes/Errores.tsx";
import { AvisoHecho } from "../componentes/AvisoHecho.tsx";
import { cargaDecision, mensajeDeDecision } from "../componentes/decision-modelo.ts";
import { candidatosDelBanco } from "../componentes/faltantes-modelo.ts";
import { Aviso, Boton, Seccion, Tarjeta, Vacio, cx } from "../componentes/ui.tsx";

/**
 * Pagos que tu sistema da por cobrados y que el banco no muestra.
 *
 * Conciliar responde «¿de quién es esta plata que entró?»; esto responde
 * «¿dónde está la plata que dijimos que entró?», que es la pregunta que
 * destapa un faltante.
 *
 * Hasta SPEC-029 la lista solo crecía: Lorus llegó a 57 sin forma de cerrar
 * ninguno, y un aviso que no se puede atender enseña a no leerlo. Ahora cada
 * uno tiene dos salidas (§13): «Llegó en este pago», que lo liga a un pago del
 * banco, y «Ya lo revisé», que lo saca de la lista diciendo por qué. Ninguna
 * borra nada.
 *
 * En filas, como la bandeja: con tarjetas grandes, 57 eran treinta pantallas.
 */
export function Alertas() {
  const { data: libro, isPending, error, refetch } = usarLibro();
  const decidir = usarDecidir();
  const revisar = usarRevisarAlerta();
  const { puedo } = usarSesion();
  const [hecho, setHecho] = useState<{ texto: string; claveMovimiento?: string; id: number } | null>(null);

  const alertas = libro?.alertas ?? [];
  const revisadas = libro?.revisadas ?? [];
  const enRiesgo = alertas.reduce((s, h) => s + (h.montoCentavos ?? 0), 0);

  if (error) return <ErrorConsulta error={error} alReintentar={() => void refetch()} />;

  const ligar = (h: Huerfano, m: Movimiento) => {
    const d = cargaDecision({ clave: m.clave, accion: "LIGAR", abonos: [h.clave] });
    decidir.mutate(d, {
      onSuccess: () => setHecho({ texto: mensajeDeDecision(d, m.contraparte), claveMovimiento: m.clave, id: Date.now() }),
    });
  };
  const deshacer = (clave: string) => {
    const d: Decision = cargaDecision({ clave, accion: "ANULAR" });
    decidir.mutate(d, { onSuccess: () => setHecho({ texto: mensajeDeDecision(d, ""), id: Date.now() }) });
  };
  const darPorRevisado = (h: Huerfano, razon: RazonRevision, nota: string) =>
    revisar.mutate(
      { clave: h.clave, razon, nota },
      {
        onSuccess: () =>
          setHecho({
            texto: `Listo: el pago registrado de ${h.cliente || "ese cliente"} quedó revisado («${RAZON_REVISION[razon]}»). Ya no se avisará.`,
            id: Date.now(),
          }),
      },
    );

  return (
    <Seccion
      titulo="Pagos registrados que no han llegado al banco"
      descripcion="Tu sistema los da por cobrados y el banco no muestra ninguna entrada que les corresponda."
    >
      {hecho && (
        <AvisoHecho
          key={hecho.id}
          texto={hecho.texto}
          alDeshacer={hecho.claveMovimiento ? () => deshacer(hecho.claveMovimiento!) : undefined}
          deshaciendo={decidir.isPending && decidir.variables?.accion === "ANULAR"}
        />
      )}
      {(decidir.error || revisar.error) && <ErrorConsulta error={decidir.error ?? revisar.error} />}

      {alertas.length > 0 && (
        <Aviso
          tono="peligro"
          titulo={`${alertas.length} ${plural(alertas.length, "pago registrado", "pagos registrados")} por ${pesos(enRiesgo)}`}
          icono={TriangleAlert}
        >
          No significa que falte el dinero: puede haberse registrado por adelantado, ser un error al
          registrarlo o llegar por otro medio. Si lo encuentras en el banco, «Llegó en este pago»; si
          ya sabes qué pasó, «Ya lo revisé».
        </Aviso>
      )}

      {isPending ? (
        <EsqueletoLista filas={3} />
      ) : alertas.length === 0 ? (
        <Tarjeta>
          <Vacio icono={ShieldCheck} titulo="Todo cuadra">
            Cada pago registrado tiene su entrada en el banco, o un medio de pago que explica por qué
            no debería aparecer.
          </Vacio>
        </Tarjeta>
      ) : (
        <ul className="tarjeta @container overflow-visible">
          {alertas.map((h) => (
            <FilaFaltante
              key={h.clave}
              faltante={h}
              candidatos={candidatosDelBanco(h, libro?.movimientos ?? [])}
              puedeDecidir={puedo(PERMISO.DECIDIR)}
              ocupado={decidir.isPending || revisar.isPending}
              alLigar={(m) => ligar(h, m)}
              alRevisar={(razon, nota) => darPorRevisado(h, razon, nota)}
            />
          ))}
        </ul>
      )}

      {revisadas.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer text-xs font-medium text-tenue hover:text-tinta">
            {revisadas.length} {plural(revisadas.length, "revisado", "revisados")}
          </summary>
          <ul className="mt-2 space-y-1.5">
            {revisadas.map((h) => (
              <li key={h.clave} className="flex flex-wrap items-baseline gap-x-2 text-tenue">
                <span className="cifra font-medium text-tinta">{pesos(h.montoCentavos)}</span>
                <span>{h.cliente}</span>
                {h.factura && <span className="cifra">· {h.factura}</span>}
                {h.revision && (
                  <span className="text-xs">
                    — {RAZON_REVISION[h.revision.razon]}
                    {h.revision.nota ? `: ${h.revision.nota}` : ""} · {h.revision.quien} ·{" "}
                    {haceRato(h.revision.cuando)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
    </Seccion>
  );
}

function FilaFaltante({
  faltante: h,
  candidatos,
  puedeDecidir,
  ocupado,
  alLigar,
  alRevisar,
}: {
  faltante: Huerfano;
  candidatos: Movimiento[];
  puedeDecidir: boolean;
  ocupado?: boolean;
  alLigar: (m: Movimiento) => void;
  alRevisar: (razon: RazonRevision, nota: string) => void;
}) {
  const [modo, setModo] = useState<"ligar" | "revisar" | null>(null);
  const [razon, setRazon] = useState<RazonRevision | null>(null);
  const [nota, setNota] = useState("");
  const alternar = (m: "ligar" | "revisar") => setModo((v) => (v === m ? null : m));

  return (
    <li className="border-t border-borde first:border-t-0">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5 @3xl:grid @3xl:grid-cols-[8rem_minmax(0,1fr)_auto]">
        <div className="min-w-24">
          <p className="cifra text-[15px] font-semibold text-tinta">{pesos(h.montoCentavos)}</p>
          <p className="text-[11px] whitespace-nowrap text-tenue-2">registrado el {fechaCorta(h.fechaPago)}</p>
        </div>
        <div className="min-w-0 basis-full text-[13px] leading-snug @3xl:basis-auto">
          <p className="truncate text-tinta">{h.cliente || "—"}</p>
          <p className="truncate text-xs text-tenue">
            {[h.factura, h.metodo].filter(Boolean).join(" · ") || "sin factura ni medio de pago"}
          </p>
        </div>
        {puedeDecidir && (
          <div className="ml-auto flex items-center gap-1.5">
            <Boton tamano="sm" variante={modo === "ligar" ? "suave" : "primario"} icono={Link2} onClick={() => alternar("ligar")}>
              Llegó en este pago
            </Boton>
            <Boton tamano="sm" variante="fantasma" onClick={() => alternar("revisar")}>
              Ya lo revisé
              <ChevronDown className={cx("size-3.5 transition-transform", modo === "revisar" && "rotate-180")} aria-hidden />
            </Boton>
          </div>
        )}
      </div>

      {modo === "ligar" && (
        <div className="space-y-2 border-t border-borde bg-superficie-2/50 px-4 py-3 text-sm">
          {candidatos.length ? (
            <>
              <p className="text-xs text-tenue">
                Pagos del banco sin decidir, de monto parecido y cercanos en fecha. ¿Es alguno?
              </p>
              <ul className="space-y-1.5">
                {candidatos.map((m) => {
                  const dif = (m.montoCentavos ?? 0) - (h.montoCentavos ?? 0);
                  const hora = soloHora(m.fecha);
                  return (
                    <li key={m.clave} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="cifra w-24 font-medium text-tinta">{pesos(m.montoCentavos)}</span>
                      <span className="text-xs text-tenue-2">
                        {fechaCorta(m.fecha)}
                        {hora ? ` · ${hora}` : ""}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{m.contraparte || "el banco no dice quién"}</span>
                      {dif !== 0 && (
                        <span className="cifra text-xs text-aviso-tinta">
                          {dif < 0 ? "faltan" : "sobran"} {pesos(Math.abs(dif))}
                        </span>
                      )}
                      <Boton tamano="sm" variante="suave" cargando={ocupado} onClick={() => alLigar(m)}>
                        Es este
                      </Boton>
                    </li>
                  );
                })}
              </ul>
            </>
          ) : (
            <p className="text-xs text-tenue">
              No hay pagos del banco sin decidir que se le parezcan en monto y fecha. Si ya sabes qué
              pasó, usa «Ya lo revisé».
            </p>
          )}
        </div>
      )}

      {modo === "revisar" && (
        <div className="space-y-2.5 border-t border-borde bg-superficie-2/50 px-4 py-3 text-sm">
          <p className="text-xs text-tenue">¿Qué pasó con este pago? Queda anotado con tu nombre y deja de avisarse.</p>
          <div className="flex flex-wrap gap-1.5">
            {(Object.keys(RAZON_REVISION) as RazonRevision[]).map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setRazon(r)}
                className={cx(
                  "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                  razon === r
                    ? "border-marca bg-marca-suave text-marca-tinta"
                    : "border-borde bg-superficie text-tenue hover:border-borde-fuerte",
                )}
              >
                {RAZON_REVISION[r]}
              </button>
            ))}
          </div>
          <input
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            placeholder="Detalle (opcional): pagó en efectivo, se registró dos veces…"
            className="h-9 w-full rounded-lg border border-borde bg-superficie px-3 text-sm placeholder:text-tenue-2 focus:border-marca focus:outline-none"
          />
          <Boton tamano="sm" variante="suave" disabled={!razon} cargando={ocupado} onClick={() => razon && alRevisar(razon, nota)}>
            Dar por revisado
          </Boton>
        </div>
      )}
    </li>
  );
}
